// src/components/PeriodSelector.js
import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import { Tune } from "@mui/icons-material";

// Затухание у правого края, пока список не пролистан до конца
const FADE_MASK = "linear-gradient(90deg, #000 85%, transparent)";

/**
 * Горизонтальный выбор периода с прокруткой.
 * showFilters — управляет отображением кнопки фильтров.
 * rangeLabel — даты выбранного периода («21.10», «16.10 – 21.10»): ночью, пока смены открыты,
 *   объясняет, почему «Сегодня» — вчерашняя календарная дата.
 */
const PeriodSelector = ({ period, onChange, onOpenFilters, showFilters = true, rangeLabel = "", dayItem = null }) => {
  // dayItem — разовый день из ссылки ({ key: "day", label: "24.10" }): первым пунктом, активный; выбор другого
  // периода его убирает (страница), в localStorage не сохраняется
  const items = [
    ...(dayItem ? [dayItem] : []),
    { key: "today", label: "Сегодня" },
    { key: "yesterday", label: "Вчера" },
    { key: "week", label: "Неделя" },
    { key: "month", label: "Месяц" },
    { key: "lastWeek", label: "Прошлая неделя" },
    { key: "lastMonth", label: "Прошлый месяц" },
  ];

  const listRef = useRef(null);
  const itemRefs = useRef({});
  // true — справа есть скрытые пункты (показываем маску)
  const [hasMoreRight, setHasMoreRight] = useState(false);

  const updateFade = () => {
    const el = listRef.current;
    if (!el) return;
    setHasMoreRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  };

  useEffect(() => {
    updateFade();
    window.addEventListener("resize", updateFade);
    return () => window.removeEventListener("resize", updateFade);
  }, []);

  // Активный период — в видимой области (при загрузке и смене; подпись дат расширяет пункт)
  useEffect(() => {
    itemRefs.current[period]?.scrollIntoView({ inline: "nearest", block: "nearest" });
    updateFade();
  }, [period, rangeLabel]);

  const handleClick = (key) => {
    if (dayItem && key === dayItem.key) return; // разовый день уже выбран
    localStorage.setItem("selectedPeriod", key); // 💾 сохраняем выбор периода
    onChange(key);
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        width: { xs: "100%", md: "auto" },
        maxWidth: { xs: 1000, md: "none" },
        flex: { md: 1 },
        // На десктопе уступает место выбору ресторана и вкладкам слева: не хватает места — периоды прокручиваются
        minWidth: { md: 0 },
        justifyContent: { xs: "space-between", md: "flex-end" },
        px: { xs: 2, md: 0 },
      }}
    >
      {/* Горизонтальный список периодов */}
      <Box
        ref={listRef}
        onScroll={updateFade}
        sx={{
          display: "flex",
          flexGrow: 1,
          minWidth: 0,
          // Прижаты вправо отступом первого пункта, а не justify-content: flex-end — с ним при нехватке места
          // начало списка уходит влево за край и до него не долистать
          justifyContent: "flex-start",
          overflowX: "auto",
          scrollSnapType: "x proximity",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": { display: "none" },
          ...(hasMoreRight && { maskImage: FADE_MASK, WebkitMaskImage: FADE_MASK }),
          gap: 3,
          py: 1,
        }}
      >
        {items.map((item, index) => (
          <Typography
            key={item.key}
            ref={(el) => {
              itemRefs.current[item.key] = el;
            }}
            onClick={() => handleClick(item.key)}
            sx={{
              flexShrink: 0,
              ml: index === 0 ? { md: "auto" } : 0,
              scrollSnapAlign: "start",
              fontSize: "1rem",
              fontWeight: period === item.key ? 700 : 400,
              color: period === item.key ? "#FFFFFF" : "#8e8e93",
              cursor: "pointer",
              userSelect: "none",
              whiteSpace: "nowrap",
              transition: "all 0.25s ease",
              "&:hover": {
                color: "#fff",
              },
            }}
          >
            {item.label}
            {period === item.key && rangeLabel && (
              <Box
                component="span"
                sx={{ ml: 0.75, fontSize: "0.75rem", fontWeight: 500, color: "#8e8e93", fontVariantNumeric: "tabular-nums" }}
              >
                {rangeLabel}
              </Box>
            )}
          </Typography>
        ))}
      </Box>

      {/* Кнопка фильтров — отображается только если showFilters === true */}
      {showFilters && (
        <IconButton
          onClick={onOpenFilters}
          sx={{
            ml: 1,
            color: "#fff",
            backgroundColor: "rgba(255,255,255,0.08)",
            borderRadius: "14px",
            p: 1,
            flexShrink: 0,
            transition: "all 0.25s ease",
            "&:hover": {
              backgroundColor: "rgba(255,255,255,0.15)",
              transform: "scale(1.05)",
            },
          }}
        >
          <Tune fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};

export default PeriodSelector;
